import { useMemo } from 'react';
import useViewerIdentity from './useViewerIdentity';

const STATUS_MESSAGES = {
  MUTED: 'You have been muted by a moderator. You cannot create posts, comments, or other content until this is lifted.',
  SUSPENDED: 'Your account has been suspended. You cannot create posts, comments, or other content until this is resolved.',
  BANNED: 'Your account has been banned. You cannot create posts, comments, or other content.',
};

const RESTRICTED_STATUSES = ['MUTED', 'SUSPENDED', 'BANNED'];

const formatExpiry = (expiresAt) => {
  if (!expiresAt) return null;
  const date = new Date(expiresAt);
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  if (diffMs <= 0) return null;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days >= 1) return ` Expires in ${days} day${days > 1 ? 's' : ''}.`;
  if (hours >= 1) return ` Expires in ${hours} hour${hours > 1 ? 's' : ''}.`;
  const minutes = Math.floor(diffMs / (1000 * 60));
  return ` Expires in ${minutes} minute${minutes > 1 ? 's' : ''}.`;
};

/**
 * Hook that checks whether the current user is allowed to perform write actions
 * (create posts, comments, novels, chapters, etc.).
 *
 * Returns:
 *  - canWrite: true if the user can perform write actions
 *  - status: the user's current status (ACTIVE, MUTED, SUSPENDED, BANNED)
 *  - reason: a human-readable message explaining why the user cannot write (null if canWrite is true)
 *  - isMuted: convenience boolean for muted/suspended/banned
 *  - expiresAt: ISO string when the status expires (null if permanent or ACTIVE)
 */
const useCanWrite = () => {
  const { user } = useViewerIdentity();

  return useMemo(() => {
    // Only treat explicitly restricted statuses as non-writeable.
    // If status is missing, null, undefined, or any unknown value, default to ACTIVE.
    const rawStatus = user?.status;
    const status = RESTRICTED_STATUSES.includes(rawStatus) ? rawStatus : 'ACTIVE';
    const canWrite = status === 'ACTIVE';
    const isMuted = !canWrite;
    const expiresAt = user?.statusExpiresAt || null;
    const expiryHint = isMuted ? formatExpiry(expiresAt) : null;
    const reason = isMuted
      ? (STATUS_MESSAGES[status] || 'Your account is restricted from creating content.') + (expiryHint || '')
      : null;

    return { canWrite, status, reason, isMuted, expiresAt };
  }, [user?.status, user?.statusExpiresAt]);
};

export default useCanWrite;
